/**
 * Canonical error codes used across router `/internal/*` contracts (ADR-004).
 * Every non-2xx response from an internal endpoint carries an {@link ErrorEnvelope}.
 */
export type ErrorCode =
  | 'AUTH_REQUIRED'
  | 'KEY_NOT_FOUND'
  | 'API_KEY_NOT_FOUND'
  | 'ACCOUNT_NOT_FOUND'
  | 'ACCOUNT_SUSPENDED'
  | 'ACCOUNT_DELETED'
  | 'ACCOUNT_ALREADY_SUSPENDED'
  | 'ACCOUNT_NOT_SUSPENDED'
  | 'ACCOUNT_ALREADY_DELETED'
  | 'INSUFFICIENT_BALANCE'
  | 'SERVICE_SUSPENDED'
  | 'MEMBER_MODEL_FORBIDDEN'
  | 'MEMBER_MONTHLY_LIMIT_EXCEEDED'
  | 'GIFT_CARD_ALREADY_REDEEMED'
  | 'GIFT_CARD_CODE_EXISTS'
  | 'GIFT_CARD_EXPIRED'
  | 'GIFT_CARD_BATCH_CLOSED'
  | 'GIFT_CARD_BATCH_EXISTS'
  | 'GIFT_CARD_BATCH_NOT_FOUND'
  | 'GIFT_CARD_NOT_FOUND'
  | 'GIFT_CARD_REVOKED'
  | 'TICKET_NOT_FOUND'
  | 'TICKET_CLOSED'
  | 'TICKET_LIMIT_EXCEEDED'
  | 'MODEL_NOT_FOUND'
  | 'MODEL_ALREADY_EXISTS'
  | 'MODEL_ALIAS_NOT_FOUND'
  | 'MODEL_ALIAS_ALREADY_EXISTS'
  | 'MODEL_MAPPING_CONFLICT'
  | 'MODEL_MAPPING_ROLLOUT_PENDING'
  | 'MODEL_MAPPING_STALE'
  | 'MODEL_ALREADY_DEPRECATED'
  | 'MODEL_NOT_DEPRECATED'
  | 'MODEL_DELISTED'
  | 'MODEL_PRICING_NOT_FOUND'
  | 'MODEL_PRICING_RATED_ANCHOR_CONFLICT'
  | 'MODEL_UNIT_PRICING_RATED_ANCHOR_CONFLICT'
  /** The anchor guard cannot see the whole rated timeline (reaped, `RATED_READERS_BOUNDED` on). */
  | 'MODEL_PRICING_RATED_ANCHOR_UNVERIFIABLE'
  | 'MODEL_UNIT_PRICING_RATED_ANCHOR_UNVERIFIABLE'
  | 'MODEL_PRICING_VARIANTS_REQUIRED'
  | 'EMAIL_NOTICE_NOT_FOUND'
  | 'EMAIL_NOTICE_STATE_CONFLICT'
  /** The notice audience window reaches past the rated hot line (`RATED_READERS_BOUNDED` on). */
  | 'EMAIL_NOTICE_AUDIENCE_WINDOW_UNAVAILABLE'
  /** An ops/replay read window reaches past the rated hot line (`RATED_READERS_BOUNDED` on). */
  | 'OPS_REVENUE_WINDOW_UNAVAILABLE'
  | 'RATING_REPLAY_WINDOW_UNAVAILABLE'
  | 'RATED_READERS_WINDOW_UNAVAILABLE'
  | 'USAGE_WINDOW_UNAVAILABLE'
  /** A tenant ledger page reaches days the hot table may no longer hold and the cold layer cannot answer for yet (`LEDGER_READ_ROUTING=on`). */
  | 'LEDGER_WINDOW_UNAVAILABLE'
  | 'EMAIL_NOTICE_RECIPIENTS_NOT_FINALIZED'
  | 'EMAIL_NOTICE_TEST_SEND_REQUIRED'
  | 'EMAIL_NOTICE_TEST_SEND_FAILED'
  | 'EMAIL_NOTICE_BULK_EMAIL_UNCONFIGURED'
  | 'EMAIL_TEMPLATE_NOT_FOUND'
  | 'EMAIL_TEMPLATE_UNKNOWN_PLACEHOLDER'
  | 'PROTOCOL_ALREADY_REGISTERED'
  | 'PROTOCOL_NOT_REGISTERED'
  | 'UPSTREAM_NOT_FOUND'
  | 'CHANNEL_NOT_FOUND'
  | 'CHANNEL_ALREADY_EXISTS'
  | 'CHANNEL_ALIAS_ALREADY_EXISTS'
  | 'CHANNEL_MODEL_NOT_FOUND'
  | 'CHANNEL_MODEL_ALREADY_EXISTS'
  | 'CHANNEL_MODEL_DEPENDENCIES_EXIST'
  | 'VENDOR_NOT_FOUND'
  | 'VENDOR_ALREADY_EXISTS'
  | 'VENDOR_OFFICIAL_KEY_NOT_FOUND'
  | 'VENDOR_DEPENDENCIES_EXIST'
  | 'VENDOR_DISCOVERED_MODEL_NOT_FOUND'
  | 'VENDOR_DISCOVERED_MODEL_NOT_MANUAL'
  | 'KEY_BUCKET_NOT_FOUND'
  | 'UPSTREAM_CAPABILITY_NOT_FOUND'
  | 'UPSTREAM_CAPABILITY_IN_USE'
  | 'UPSTREAM_CAPABILITY_NOT_ROUTABLE'
  | 'UPSTREAM_SKU_COST_NOT_CONFIGURED'
  | 'ROUTE_BINDING_NOT_FOUND'
  | 'ROUTE_BINDING_ALREADY_EXISTS'
  | 'TARGET_PROBE_REGION_MISMATCH'
  | 'ROUTING_SNAPSHOT_STALE'
  | 'CASCADE_CONFIRMATION_REQUIRED'
  | 'ORG_ROUTING_WRITE_FORBIDDEN'
  | 'ROUTE_CONFIG_ARCHIVED'
  | 'ROUTE_CONFIG_DEPRECATED'
  | 'ROUTING_DR_PLAN_NOT_FOUND'
  | 'ROUTING_PREFLIGHT_TOKEN_STALE'
  | 'ROUTING_SWITCH_NO_ROLLBACK_TARGET'
  | 'ROUTING_SWITCH_HEAD_MOVED'
  | 'ROUTING_OVERRIDE_NOT_ENGAGED'
  | 'SERVICE_LEVEL_NOT_FOUND'
  | 'SERVICE_LEVEL_NAME_TAKEN'
  | 'SERVICE_LEVEL_ARCHIVED'
  | 'SERVICE_LEVEL_IN_USE'
  | 'SERVICE_LEVEL_ORG_ADMIN_MANAGED'
  | 'ORG_ROLE_NOT_FOUND'
  | 'ORG_ROLE_NAME_TAKEN'
  | 'ORG_ROLE_IN_USE'
  | 'FIREWALL_RULESET_NOT_FOUND'
  | 'FIREWALL_RULESET_VERSION_NOT_FOUND'
  | 'FIREWALL_RULESET_NAME_TAKEN'
  | 'FIREWALL_RULESET_INVALID'
  | 'FIREWALL_RULESET_IN_USE'
  | 'FIREWALL_RULESET_REPLAY_REQUIRED'
  | 'EGRESS_PROFILE_NOT_FOUND'
  | 'EGRESS_PROFILE_ALREADY_EXISTS'
  | 'EGRESS_PROFILE_IN_USE'
  | 'EGRESS_POLICY_NOT_FOUND'
  | 'EGRESS_POLICY_IN_USE'
  | 'EGRESS_POLICY_VERSION_CONFLICT'
  | 'EGRESS_LINK_NOT_FOUND'
  | 'EGRESS_TEMPLATE_NOT_FOUND'
  | 'EGRESS_TEMPLATE_ALREADY_EXISTS'
  | 'EGRESS_TEMPLATE_IN_USE'
  | 'PROBE_JOB_NOT_FOUND'
  | 'PROBE_JOB_NOT_CLAIMABLE'
  | 'PROBE_JOB_CLAIM_CONFLICT'
  | 'PROBE_JOB_NOT_RUNNING'
  | 'MESSAGE_TRACE_NOT_FOUND'
  | 'MESSAGE_TRACE_STORAGE_UNCONFIGURED'
  | 'REQUEST_LIFECYCLE_NOT_FOUND'
  | 'WEBHOOK_EVENT_NOT_FOUND'
  | 'ATTACHMENT_NOT_FOUND'
  | 'OPS_USER_NOT_FOUND'
  | 'OPS_USER_ALREADY_EXISTS'
  | 'OPS_USER_ALREADY_DISABLED'
  | 'OPS_USER_NOT_DISABLED'
  | 'OPS_GITHUB_ID_ALREADY_BOUND'
  | 'OPS_FEISHU_ID_ALREADY_BOUND'
  | 'VPS_CONFIG_NOT_FOUND'
  | 'VPS_LB_POOL_NOT_FOUND'
  | 'VPS_LB_POOL_ENV_MISMATCH'
  | 'VPS_LB_FLOOR_VIOLATION'
  | 'VPS_LB_DEPLOY_TARGET_EXCLUDED'
  | 'VPS_READER_ADMISSION_REQUIRED'
  | 'VPS_CONFIG_IDENTITY_IMMUTABLE'
  | 'VPS_CONFIG_NO_DEPLOY_IP'
  | 'VPS_LB_MUTATION_BUSY'
  | 'VPS_LB_CF_ERROR'
  | 'VPS_CONFIG_STILL_IN_POOL'
  | 'SALES_PROFILE_NOT_FOUND'
  | 'SALES_PROFILE_HAS_ACTIVE_CUSTOMERS'
  | 'APPROVAL_REQUEST_NOT_FOUND'
  | 'APPROVAL_REQUEST_NOT_PENDING'
  | 'APPROVAL_DUPLICATE_OPEN_REQUEST'
  | 'CUSTOMER_TRANSFER_STALE'
  | 'GIFT_QUOTA_EXCEEDED'
  | 'GIFT_OVER_QUOTA_STALE'
  | 'SKU_RATE_NOT_CONFIGURED'
  | 'SKU_RATE_VISIBLE_CANNOT_CLEAR'
  | 'SKU_DISCOUNT_APPROVAL_PENDING'
  | 'SKU_RATE_REQUIRED_WHILE_VISIBLE'
  | 'SKU_RATE_BELOW_SALES_FLOOR'
  | 'SKU_SHELF_NOT_PRICED'
  | 'SKU_SHELF_NOT_ROUTABLE'
  | 'SKU_SHELF_READINESS_UNAVAILABLE'
  | 'NAMESPACE_RATE_BELOW_SKU_FLOOR'
  | 'DISCOUNT_BELOW_SALES_COST_STALE'
  | 'CREDIT_APPLICATION_STALE'
  | 'EXCHANGE_RATE_POLICY_NOT_CONFIGURED'
  | 'TEMPORARY_CREDIT_EVIDENCE_REQUIRED'
  | 'TEMPORARY_CREDIT_CAP_EXCEEDED'
  | 'TEMPORARY_CREDIT_LONG_TERM_EXISTS'
  | 'LONG_TERM_CREDIT_TEMPORARY_EXISTS'
  | 'TEMPORARY_CREDIT_APPLICATION_STALE'
  | 'CREDIT_NEW_GRANT_FROZEN'
  | 'CREDIT_EXEMPTION_EXPIRES_REQUIRED'
  | 'CREDIT_EXEMPTION_REASON_REQUIRED'
  | 'CREDIT_EXEMPTION_EXPIRES_NOT_FUTURE'
  | 'CREDIT_EXEMPTION_NOT_LIVE'
  | 'OPS_USER_PASSWORD_ROLE_DENIED'
  | 'REQUIRES_DUAL_SIGN'
  | 'APPROVAL_STAGE_ACTOR_CONFLICT'
  | 'RECEIPT_ORDER_NOT_FOUND'
  | 'RECEIPT_ORDER_NOT_SUBMITTED'
  | 'RECEIPT_ORDER_NOT_CREDITED'
  | 'RECEIPT_INVOICE_NOT_OPEN'
  | 'SIGNUP_BRAND_CONFLICT'
  | 'FUNDING_BATCH_NOT_FOUND'
  | 'CUSTOMER_ALREADY_CLAIMED'
  | 'ORG_NOT_FOUND'
  | 'INVITATION_NOT_FOUND'
  | 'INVITATION_CONFLICT'
  | 'KEY_PRESET_VERSION_CONFLICT'
  | 'KEY_PRESET_OPERATION_NOT_FOUND'
  | 'KEY_PRESET_MAPPING_CONFLICT'
  | 'KEY_PRESET_OPERATION_CONFLICT'
  | 'KEY_PRESET_ROLLBACK_CONFLICT'
  | 'SKU_ACTIVATION_ENVIRONMENT_MISMATCH'
  | 'SKU_ACTIVATION_SCOPE_MISMATCH'
  | 'SKU_ACTIVATION_PLAN_NOT_FOUND'
  | 'SKU_ACTIVATION_PLAN_CONFLICT'
  | 'INVITATION_EXPIRED'
  | 'INVITATION_REVOKED'
  | 'INVITATION_ALREADY_ACCEPTED'
  | 'INVALID_VERIFICATION_TOKEN'
  | 'VERIFICATION_TOKEN_EXPIRED'
  | 'VERIFICATION_TOKEN_CONSUMED'
  | 'INVITE_CODE_INVALID'
  | 'INVITE_CODE_QUOTA_EXHAUSTED'
  | 'EMAIL_ALREADY_REGISTERED'
  | 'EMAIL_FORMAT_INVALID'
  | 'PASSWORD_LENGTH_INVALID'
  | 'PASSWORD_ALREADY_SET'
  | 'CODE_INVALID'
  | 'CODE_EXPIRED'
  | 'CODE_RATE_LIMITED'
  | 'INVITE_CODE_NOT_FOUND'
  | 'INVITE_CODE_FORMAT_INVALID'
  | 'INVITE_CODE_TAKEN'
  | 'INVITE_CODE_HAS_LEGACY_DISCOUNTS'
  | 'INVITE_CODE_ALREADY_HAS_PLAN'
  | 'PROMOTER_CODE_INVALID'
  | 'PROMOTER_ALREADY_BOUND'
  | 'PROMOTER_BIND_WINDOW_CLOSED'
  | 'PROMOTER_SELF_BIND_FORBIDDEN'
  | 'PROMOTER_BIND_CONFLICT'
  | 'PROMOTER_POSITION_REQUIRED'
  | 'PROMOTER_POSITION_READ_ONLY'
  | 'PROMOTER_CODE_TAKEN'
  | 'PROMOTER_CODE_BLACKLISTED'
  | 'PROMOTER_CODE_REMINT_TOO_SOON'
  | 'PROMOTER_SPLIT_RATIO_INVALID'
  | 'PROMOTER_TARGET_NOT_FOUND'
  | 'PROMOTER_TARGET_HOLDS_POSITION'
  | 'PROMOTER_AGREEMENT_REQUIRED'
  | 'MODEL_PLAN_NOT_FOUND'
  | 'QUOTATION_NOT_FOUND'
  | 'MODEL_PLAN_NAME_CONFLICT'
  | 'MODEL_PLAN_ARCHIVED'
  | 'MODEL_PLAN_HAS_ACTIVE_CODES'
  | 'MODEL_PLAN_RATE_BELOW_SALES_FLOOR'
  | 'MODEL_PLAN_SKU_OUTSIDE_GROUPS'
  | 'MODEL_PLAN_MANAGED'
  | 'EMAIL_NOT_VERIFIED'
  | 'PAYMENT_ORDER_NOT_FOUND'
  | 'PAYMENT_PROVIDER_CONFIG_NOT_FOUND'
  | 'TOPUP_AMOUNT_TOO_SMALL'
  | 'TOPUP_AMOUNT_TOO_LARGE'
  | 'TOPUP_PROVIDER_ERROR'
  | 'INVALID_PAYLOAD'
  | 'ARCHIVED_WINDOW_FROZEN'
  | 'COMMISSION_RATE_RETROACTIVE'
  | 'COMMISSION_RATE_ALREADY_EXISTS'
  | 'COMMISSION_STATEMENT_NOT_FOUND'
  | 'COMMISSION_STATEMENT_NOT_DRAFT'
  | 'COMMISSION_STATEMENT_PERIOD_OPEN'
  | 'COMMISSION_STATEMENT_NOT_CONFIRMED'
  | 'COMMISSION_STATEMENT_ALREADY_PAID_OUT'
  | 'COMMISSION_ADJUSTMENT_IDEMPOTENCY_CONFLICT'
  | 'LEDGER_ARCHIVE_DAY_NOT_STOPPED'
  | 'LEDGER_ARCHIVE_RESTART_REFUSED'
  | 'LEDGER_ARCHIVE_L1_RELEASE_REFUSED'
  | 'LEDGER_ARCHIVE_REEXPORT_REFUSED'
  | 'LEDGER_WRITE_WINDOW_NOT_FOUND'
  | 'LEDGER_WRITE_WINDOW_REFUSED'
  | 'LEDGER_WRITE_WINDOW_CONFIRMATION_MISMATCH'
  | 'REVENUE_DAILY_UNAVAILABLE'
  | 'PNL_USAGE_DAY_UNSEALED'
  | 'REVENUE_PARTIAL_HOUR_UNAVAILABLE'
  | 'INVALID_CREDENTIALS'
  | 'OPS_USER_DISABLED'
  | 'FORBIDDEN'
  | 'VPS_DISCOVERY_MISSING_DEPLOY_IP'
  | 'NOT_IMPLEMENTED'
  | 'RATE_LIMITED'
  | 'SERVICE_UNAVAILABLE'
  // Internal gateway answer: this read window holds a day only the cold tier can answer and the
  // caller did not ask for it (usage-agg-cold-fill.ts). Only emitted under USAGE_AGG_COLD_FILL=on.
  | 'HISTORY_READ_COLD_UNSUPPORTED'
  // Internal gateway answer: a history read hit its statement budget (SQLSTATE 57014). Distinct from
  // SERVICE_UNAVAILABLE on purpose: platform retries that one, and retrying a read that already ran
  // out of time only repeats the load. Never retryable.
  | 'HISTORY_READ_TIMEOUT'
  | 'INTERNAL';

/**
 * The literal set of HTTP status codes that `errorResponse` can produce.
 * Narrow union so Hono `c.json(body, status)` accepts `err.status` without a cast.
 */
export type ErrorStatusCode =
  | 400
  | 401
  | 402
  | 403
  | 404
  | 409
  | 410
  | 429
  | 500
  | 501
  | 502
  | 503;

export interface ErrorEnvelope {
  error: {
    code: ErrorCode;
    message: string;
    retryable: boolean;
  };
}

export interface ErrorResponse {
  status: ErrorStatusCode;
  body: ErrorEnvelope;
}

/** Build the canonical response pair (status + body) for a given error code. */
export function errorResponse(
  code: ErrorCode,
  message: string,
  overrides: { status?: ErrorStatusCode; retryable?: boolean } = {},
): ErrorResponse {
  const defaults = ERROR_DEFAULTS[code];
  return {
    status: overrides.status ?? defaults.status,
    body: {
      error: {
        code,
        message,
        retryable: overrides.retryable ?? defaults.retryable,
      },
    },
  };
}

export function isErrorCode(value: unknown): value is ErrorCode {
  return typeof value === 'string' && (ERROR_CODES as readonly string[]).includes(value);
}

/**
 * PostgreSQL reports duplicate writes with SQLSTATE 23505. Wrappers may expose
 * it as `code` or `sqlState`, and may retain the driver error as a nested cause.
 * Match structured state only so conflict responses never depend on driver text.
 */
export function isUniqueViolation(err: unknown): boolean {
  if (typeof err !== 'object' || err === null) return false;
  const candidate = err as { code?: unknown; sqlState?: unknown; cause?: unknown };
  if (candidate.code === '23505' || candidate.sqlState === '23505') return true;
  return candidate.cause === undefined ? false : isUniqueViolation(candidate.cause);
}

// Defaults per ADR-004 error model table. `retryable: true` on transient
// classes (INTERNAL, SERVICE_UNAVAILABLE, RATE_LIMITED) — callers retry
// after short backoff (or after the `Retry-After` header for 429). Other
// 4xx family codes are permanent, and NOT_IMPLEMENTED (501) is a bootstrap
// stub signal.
const ERROR_DEFAULTS: Record<ErrorCode, { status: ErrorStatusCode; retryable: boolean }> = {
  AUTH_REQUIRED: { status: 401, retryable: false },
  KEY_NOT_FOUND: { status: 404, retryable: false },
  API_KEY_NOT_FOUND: { status: 404, retryable: false },
  ACCOUNT_NOT_FOUND: { status: 404, retryable: false },
  // 403 not 401 because the key is valid but the account is disabled.
  // `retryable` stays false so the gateway client doesn't replay blindly.
  ACCOUNT_SUSPENDED: { status: 403, retryable: false },
  ACCOUNT_DELETED: { status: 403, retryable: false },
  ACCOUNT_ALREADY_SUSPENDED: { status: 409, retryable: false },
  ACCOUNT_NOT_SUSPENDED: { status: 409, retryable: false },
  ACCOUNT_ALREADY_DELETED: { status: 409, retryable: false },
  INSUFFICIENT_BALANCE: { status: 402, retryable: false },
  // Per-customer service kill switch (epic #1033 E4-C7, #1131): the account
  // is administratively suspended from model traffic by a sales lead / admin.
  // 403 (not 402) so it is unambiguously distinct from INSUFFICIENT_BALANCE —
  // topping up does not clear it. `retryable: false`: the caller must not
  // replay until an operator lifts the flag.
  SERVICE_SUSPENDED: { status: 403, retryable: false },
  MEMBER_MODEL_FORBIDDEN: { status: 403, retryable: false },
  MEMBER_MONTHLY_LIMIT_EXCEEDED: { status: 403, retryable: false },
  GIFT_CARD_ALREADY_REDEEMED: { status: 409, retryable: false },
  GIFT_CARD_CODE_EXISTS: { status: 409, retryable: false },
  GIFT_CARD_EXPIRED: { status: 410, retryable: false },
  GIFT_CARD_BATCH_CLOSED: { status: 409, retryable: false },
  GIFT_CARD_BATCH_EXISTS: { status: 409, retryable: false },
  GIFT_CARD_BATCH_NOT_FOUND: { status: 404, retryable: false },
  GIFT_CARD_NOT_FOUND: { status: 404, retryable: false },
  GIFT_CARD_REVOKED: { status: 410, retryable: false },
  TICKET_NOT_FOUND: { status: 404, retryable: false },
  TICKET_CLOSED: { status: 409, retryable: false },
  TICKET_LIMIT_EXCEEDED: { status: 429, retryable: true },
  MODEL_NOT_FOUND: { status: 404, retryable: false },
  MODEL_ALREADY_EXISTS: { status: 409, retryable: false },
  MODEL_ALIAS_NOT_FOUND: { status: 404, retryable: false },
  MODEL_ALIAS_ALREADY_EXISTS: { status: 409, retryable: false },
  MODEL_MAPPING_CONFLICT: { status: 409, retryable: false },
  MODEL_MAPPING_ROLLOUT_PENDING: { status: 503, retryable: true },
  MODEL_MAPPING_STALE: { status: 409, retryable: true },
  MODEL_ALREADY_DEPRECATED: { status: 409, retryable: false },
  MODEL_NOT_DEPRECATED: { status: 409, retryable: false },
  MODEL_DELISTED: { status: 409, retryable: false },
  MODEL_PRICING_NOT_FOUND: { status: 404, retryable: false },
  // A pricing generation was posted at an instant that would re-anchor
  // already-rated events (#1710). Money does not move — the rates may even be
  // identical — but the rated rows' `pricing_effective_from` would resolve to
  // a different generation on re-rate, so the replay-diff gate that guards
  // the charge equivalence claim reports drift it cannot attribute.
  // 409, not 400: the payload is well-formed and would have been accepted
  // before those events were rated, so this is a state conflict — the same
  // shape as MODEL_ALREADY_EXISTS on the append-only timeline.
  MODEL_PRICING_RATED_ANCHOR_CONFLICT: { status: 409, retryable: false },
  // The UNIT-lane twin of the guard above: `model_unit_pricing` has its own
  // append-only timeline and its own event-time resolver
  // (`rating/event-time-pricing.ts`, greatest `effective_from <= event_at`),
  // so a unit generation inserted at or before an already-rated event would
  // re-anchor that event on re-rate exactly as a token generation would —
  // the same money defect through the other price table. Same status and
  // retryability for the same state-conflict reason.
  MODEL_UNIT_PRICING_RATED_ANCHOR_CONFLICT: { status: 409, retryable: false },
  // The anchor guard was asked about an instant older than the rated hot line
  // while `RATED_READERS_BOUNDED` is on: the latest rated event for the model
  // may have been reaped already, so "no rated event at or after this instant"
  // cannot be proven from `usage_rated_events`. Money does not move; the write
  // is refused rather than admitted on an unverifiable guard. 409 for the same
  // state-conflict reason as the conflict above, and the operator's repair is
  // to choose an `effective_from` inside the line (or wait for the aggregate
  // anchor source that replaces this read).
  MODEL_PRICING_RATED_ANCHOR_UNVERIFIABLE: { status: 409, retryable: false },
  MODEL_UNIT_PRICING_RATED_ANCHOR_UNVERIFIABLE: { status: 409, retryable: false },
  // The window's start is older than the rated hot line, so the answer would
  // be computed from a partially reaped table. 409: the range is valid but
  // cannot be answered in this state; the repair is a narrower window.
  OPS_REVENUE_WINDOW_UNAVAILABLE: { status: 409, retryable: false },
  RATING_REPLAY_WINDOW_UNAVAILABLE: { status: 409, retryable: false },
  RATED_READERS_WINDOW_UNAVAILABLE: { status: 409, retryable: false },
  // A usage_logs reader whose window reaches days the hot table no longer holds
  // and that no other layer can answer for it (usage-hot-window.ts).
  USAGE_WINDOW_UNAVAILABLE: { status: 409, retryable: false },
  // A tenant transactions page whose window reaches booking days past the ledger retention line that
  // the cold layer has not cleared for reading: the answer would be short, so it is refused instead.
  LEDGER_WINDOW_UNAVAILABLE: { status: 409, retryable: false },
  // A pricing generation was posted WITHOUT `variants` for a model whose
  // stored metering spec declares a token-lane tier axis (#1937).
  //
  // 400, not 409, and the distinction is load-bearing: unlike the anchor
  // conflict above, this payload would NEVER have been accepted for this
  // model. `model_pricing_variants` cascades from the base row, so a new
  // generation does not inherit the previous one's tiers — an omitted
  // `variants` is a generation with no tier priced at all, and every request
  // that resolves a tier then rates as `variant_price_missing`: delivered,
  // upstream paid, charged nothing. The body is what is wrong, and the repair
  // is to send the tier rates.
  //
  // Reachable by an operator who cannot see the dimension, which is exactly
  // why it must be an error rather than a default: the surfaces that write
  // this face carried no `variants` field at all until #1937's ops round, so
  // "the caller will remember" was never an available guarantee.
  MODEL_PRICING_VARIANTS_REQUIRED: { status: 400, retryable: false },
  // Operator-authored customer mail (#1911). The lifecycle is draft → sent /
  // cancelled, and every refusal below is a STATE conflict rather than a
  // malformed payload: the same body would have been accepted one transition
  // earlier.
  EMAIL_NOTICE_NOT_FOUND: { status: 404, retryable: false },
  EMAIL_NOTICE_STATE_CONFLICT: { status: 409, retryable: false },
  // The audience's `window_days` reaches past the rated hot line while
  // `RATED_READERS_BOUNDED` is on. Freezing or previewing it would compute the
  // candidate set from a table whose older verdicts were reaped: a smaller
  // audience than the operator asked for, reported as a success. Refuse it at
  // both instants (preview and freeze) with the same code so the operator
  // shortens the window or waits for the aggregate source.
  EMAIL_NOTICE_AUDIENCE_WINDOW_UNAVAILABLE: { status: 409, retryable: false },
  // Two separate gates, deliberately not collapsed into one code. The console
  // resolves each into a different next action — "recompute and freeze the
  // list" versus "send yourself one first" — and a single code would make the
  // page guess which one it is looking at.
  EMAIL_NOTICE_RECIPIENTS_NOT_FINALIZED: { status: 409, retryable: false },
  EMAIL_NOTICE_TEST_SEND_REQUIRED: { status: 409, retryable: false },
  // The rehearsal itself was attempted and the provider REFUSED it — distinct
  // from `EMAIL_NOTICE_BULK_EMAIL_UNCONFIGURED`, which this used to reuse.
  // That code means "no credential is deployed for this brand", and reporting
  // a hard SMTP rejection (bad recipient, failed AUTH) under it told every
  // reader of `retryable` that the request would succeed unchanged once
  // someone finished a deployment. 502 because the failure is downstream of
  // us, at an external provider; `retryable: false` because the two causes we
  // can actually distinguish — a malformed address and a rejected credential —
  // are both permanent until a human changes something, and this surface is
  // driven by a human who should read the provider's reason rather than press
  // again.
  EMAIL_NOTICE_TEST_SEND_FAILED: { status: 502, retryable: false },
  // 503 + retryable: the bulk mail channel is a deployment-time credential
  // set, so an unconfigured environment is an operational gap the operator can
  // close without changing the request. Deliberately NOT a silent fallback to
  // the auth channel — see `bulk-email-branding.ts`.
  EMAIL_NOTICE_BULK_EMAIL_UNCONFIGURED: { status: 503, retryable: true },
  EMAIL_TEMPLATE_NOT_FOUND: { status: 404, retryable: false },
  // 400, matching `FIREWALL_RULESET_INVALID` — the other code for "the content
  // an operator authored does not validate". NOT 422: `ErrorStatusCode` is a
  // deliberately narrow union so Hono accepts `err.status` without a cast, and
  // widening it for one code would spend that narrowness on a distinction no
  // caller acts on.
  //
  // Not retryable, and refused at SAVE time on purpose: a placeholder is
  // substituted at send time and a send is unrecallable, so `{{ balnce }}`
  // accepted here reaches a customer's inbox with the braces still in it and
  // nothing downstream can undo that. The response names the offending words,
  // because "which one did I get wrong" is the only question the operator has.
  EMAIL_TEMPLATE_UNKNOWN_PLACEHOLDER: { status: 400, retryable: false },
  PROTOCOL_ALREADY_REGISTERED: { status: 409, retryable: false },
  PROTOCOL_NOT_REGISTERED: { status: 404, retryable: false },
  UPSTREAM_NOT_FOUND: { status: 404, retryable: false },
  WEBHOOK_EVENT_NOT_FOUND: { status: 404, retryable: false },
  ATTACHMENT_NOT_FOUND: { status: 404, retryable: false },
  OPS_USER_NOT_FOUND: { status: 404, retryable: false },
  OPS_USER_ALREADY_EXISTS: { status: 409, retryable: false },
  OPS_USER_ALREADY_DISABLED: { status: 409, retryable: false },
  OPS_USER_NOT_DISABLED: { status: 409, retryable: false },
  OPS_GITHUB_ID_ALREADY_BOUND: { status: 409, retryable: false },
  OPS_FEISHU_ID_ALREADY_BOUND: { status: 409, retryable: false },
  // VPS node ops closed loop (spec 2026-07-08, PR-2). Registry / config
  // lookups are permanent 404s; guardrail rejections (env pinning, pool
  // floor, missing deploy_ip, delete-while-in-pool) are 409 conflicts the
  // operator must resolve explicitly.
  VPS_CONFIG_NOT_FOUND: { status: 404, retryable: false },
  VPS_LB_POOL_NOT_FOUND: { status: 404, retryable: false },
  VPS_LB_POOL_ENV_MISMATCH: { status: 409, retryable: false },
  VPS_LB_FLOOR_VIOLATION: { status: 409, retryable: false },
  // Before staged SKU activation this supports an explicit force escape hatch;
  // the platform route rejects even force after forward-only Activation.
  VPS_LB_DEPLOY_TARGET_EXCLUDED: { status: 409, retryable: false },
  // Activation is forward-only: a live origin needs a durable proof that the
  // current config identity passed marker + running-container verification.
  VPS_READER_ADMISSION_REQUIRED: { status: 409, retryable: false },
  // Serving identity is create/delete lifecycle state, never an in-place
  // PATCH. This removes registry-vs-Cloudflare external mutation races.
  VPS_CONFIG_IDENTITY_IMMUTABLE: { status: 409, retryable: false },
  VPS_CONFIG_NO_DEPLOY_IP: { status: 409, retryable: false },
  // Every manual, automated, and deploy writer shares a bounded per-pool
  // lease. A competing writer retries after the current holder releases it.
  VPS_LB_MUTATION_BUSY: { status: 409, retryable: true },
  // 502 + retryable: the Cloudflare LB API is external and transient,
  // same rationale as TOPUP_PROVIDER_ERROR below.
  VPS_LB_CF_ERROR: { status: 502, retryable: true },
  VPS_CONFIG_STILL_IN_POOL: { status: 409, retryable: false },
  SALES_PROFILE_NOT_FOUND: { status: 404, retryable: false },
  // Fail-closed guard (epic #1030): deleting or re-branding a sales
  // profile while the user still owns active customers must be blocked
  // until the book is transferred.
  SALES_PROFILE_HAS_ACTIVE_CUSTOMERS: { status: 409, retryable: false },
  APPROVAL_REQUEST_NOT_FOUND: { status: 404, retryable: false },
  // Losing side of a concurrent decide/cancel on the same approval
  // request (epic #1030 E1-S2): the conditional `UPDATE … WHERE
  // status='pending'` gate matched zero rows because another actor's
  // terminal transition already committed. Not retryable — the request
  // has reached a terminal status.
  APPROVAL_REQUEST_NOT_PENDING: { status: 409, retryable: false },
  // One in-flight request per target for kinds that declare
  // `uniqueOpenPerTarget` (epic #1030 E1-S5): creation is rejected while
  // an open (pending) request of the same kind already exists for the
  // same target. Resolve or cancel the open request first.
  APPROVAL_DUPLICATE_OPEN_REQUEST: { status: 409, retryable: false },
  // Decision-time revalidation of a customer_transfer request failed
  // (epic #1030 E1-S5): the requester no longer owns the customer, or
  // the transfer target lost customer-manager authority / same-brand
  // standing since the request was submitted. The request stays pending
  // so the requester can cancel it.
  CUSTOMER_TRANSFER_STALE: { status: 409, retryable: false },
  // Targeted direct-credit gift rejected by the in-batch monthly quota
  // gate (epic #1032 E3-G2, #1103): the org's remaining calendar-month
  // gift quota cannot absorb the requested face value — including when
  // a concurrent issuance won the race for the last headroom. No money
  // moved. The caller may create a `gift_over_quota` approval instead.
  GIFT_QUOTA_EXCEEDED: { status: 409, retryable: false },
  // Decision-time revalidation of a gift_over_quota request failed
  // (epic #1032 E3-G2): the customer org / owner account no longer
  // exists, or the requester no longer owns the org — or the issuance
  // side effect lost its in-batch race. The request stays pending so
  // the requester can cancel it. Quota headroom is deliberately NOT
  // part of this check (the approval exists to exceed it).
  GIFT_OVER_QUOTA_STALE: { status: 409, retryable: false },
  // A staged/default-hidden SKU cannot enter an org's market until the
  // customer-specific price has actually been applied. A pending approval
  // does not create org_sku_rate_overrides and therefore cannot pass (#1156).
  SKU_RATE_NOT_CONFIGURED: { status: 409, retryable: false },
  // Clearing the applied customer rate while a staged SKU is effectively
  // visible would break the price-before-open invariant. Hide it first.
  SKU_RATE_VISIBLE_CANNOT_CLEAR: { status: 409, retryable: false },
  // An exact org+SKU `discount_below_sales_cost` request is still pending.
  // Even if an older override row exists, that row is not the price awaiting
  // authorization, so show remains fail-closed until the request is applied
  // or reaches another terminal status (#1156).
  SKU_DISCOUNT_APPROVAL_PENDING: { status: 409, retryable: false },
  // A default-hidden SKU has an explicit visible override. Its applied org
  // rate must be retained until the SKU is hidden again (#1156).
  SKU_RATE_REQUIRED_WHILE_VISIBLE: { status: 409, retryable: false },
  // Org SKU-rate override write from a regular-sales (CM) session landed
  // between the two pricing floors (epic #1032 E3-P3, #1106): at/above
  // the procurement hard floor but below the lead-set sales floor. The
  // override was NOT applied; the caller converts the write into a
  // `discount_below_sales_cost` approval request instead. The message
  // may reference the SALES floor only — never the procurement layer.
  SKU_RATE_BELOW_SALES_FLOOR: { status: 409, retryable: false },
  // Putting a SKU on the public shelf (`org_visibility_default = 1`, #1407)
  // exempts it from the agreed-price invariant: it sells to every org at the
  // platform list price. So the shelf write is gated on the same two facts the
  // SKU-detail readiness checklist reports, and refuses by naming which one
  // failed rather than with one opaque code.
  //
  // No current `model_pricing` row — the list price the shelf would sell at
  // does not exist, so "sold at list price" has no referent.
  SKU_SHELF_NOT_PRICED: { status: 409, retryable: false },
  // No live supply: the SKU has no enabled target, a bound capability is dead,
  // or a bound capability has no procurement cost. Shelving it advertises
  // capacity that cannot serve a request.
  SKU_SHELF_NOT_ROUTABLE: { status: 409, retryable: false },
  // The gateway could not be asked whether the SKU routes. Retryable, and
  // deliberately NOT a pass: an unreachable readiness oracle is unknown
  // readiness, and this gate fails closed on the way onto the shelf. Taking a
  // SKU off the shelf never consults it.
  SKU_SHELF_READINESS_UNAVAILABLE: { status: 503, retryable: true },
  // A namespace-grain rate write would push at least one INHERITING SKU below
  // the floor its tier must respect (2026-07-31 design §5.3). Nothing was
  // applied — the whole write is rejected, naming every offending SKU, because
  // a line rate is one number and cannot be partially agreed.
  //
  // Deliberately NOT the SKU write's convert-to-approval path, and the reason
  // is the APPROVAL'S FULFILMENT rather than merely its payload shape.
  // `discount-below-sales-cost-kind.ts` re-checks ONE SKU's floor under lock
  // and writes ONE `org_sku_rate_overrides` row. Reusing it for a line would
  // mean revalidating N floors on approval and then deciding between one
  // namespace row and N SKU rows — a new approval kind, not a parameter
  // change. (The payload is singular too: one `sku_id`, one `sku_name`, one
  // `sales_floor_multiplier`, and a target id of
  // `discountBelowSalesCostTargetId(orgId, skuId)`.)
  //
  // Refusing is also the safer commercial default, not just the cheaper one.
  // One approved line-grain discount would push N SKUs under the sales floor
  // against a single recorded number, so nobody could later read how much was
  // conceded. Pricing the offending SKUs individually makes each concession
  // carry its own approval record — and each of those writes still converts to
  // an approval on its own through the existing per-SKU path.
  NAMESPACE_RATE_BELOW_SKU_FLOOR: { status: 409, retryable: false },
  // Decision-time revalidation of a discount_below_sales_cost request
  // failed (epic #1032 E3-P3): the customer org / SKU no longer exists,
  // the requester no longer owns the org, or procurement drift pushed
  // the proposed rate under the hard cost floor — or the override
  // upsert lost its in-batch race. The request stays pending so the
  // requester can cancel it.
  DISCOUNT_BELOW_SALES_COST_STALE: { status: 409, retryable: false },
  // Decision-time revalidation of a credit_application request failed
  // (epic #1033 E4-C2, #1126; router#1999): the credit_applications row
  // is no longer `pending_approval`, the org / owner account no longer
  // exists, or the grant write lost its in-transaction race. The request
  // stays pending so the requester can cancel it. Every application now
  // needs a distinct decider — there is no within-defaults auto-approve.
  CREDIT_APPLICATION_STALE: { status: 409, retryable: false },
  // Credit submit/approve must lock a real `exchange_rate_policy` USD_CNY
  // listing rate onto the grant (router#2036). The 7.89 pricing fallback
  // is intentionally high for charging CNY; using it here would shrink
  // (or inflate) the USD ceiling relative to the signed yuan amount.
  // Missing or non-positive rate → refuse, application stays pending.
  EXCHANGE_RATE_POLICY_NOT_CONFIGURED: { status: 409, retryable: false },
  // Temporary-credit admission (router#2000): payment evidence is
  // required and may be text, an attachment, or both — an empty body
  // is refused before any row is written.
  TEMPORARY_CREDIT_EVIDENCE_REQUIRED: { status: 400, retryable: false },
  // Requested limit converts to more than ¥35,000 at the policy USD_CNY
  // rate. The customer must go through long-term credit.
  TEMPORARY_CREDIT_CAP_EXCEEDED: { status: 400, retryable: false },
  // Temporary credit is refused while an active long-term grant exists.
  // Stacking is out of scope this period.
  TEMPORARY_CREDIT_LONG_TERM_EXISTS: { status: 409, retryable: false },
  // Long-term credit is refused while an active temporary grant exists.
  // The bridge must end (repay or expire) before the contract line can
  // activate; approving must not silently supersede the other object.
  LONG_TERM_CREDIT_TEMPORARY_EXISTS: { status: 409, retryable: false },
  // Decision-time revalidation of a temporary_credit_application failed:
  // the application is no longer pending, the org/account disappeared,
  // a long-term grant appeared, or the grant write lost its race.
  TEMPORARY_CREDIT_APPLICATION_STALE: { status: 409, retryable: false },
  // Overdue ladder Decision A (router#2007): D+0 freezes new grants and
  // increases. Existing unused credit still spends. The request stays
  // pending so finance can retry after the account leaves the frozen
  // stages (settlement), not so they can force a new line through.
  CREDIT_NEW_GRANT_FROZEN: { status: 409, retryable: false },
  // Emergency exemption (router#2007): expires_at is required. A missing
  // expiry would become a permanent waiver.
  CREDIT_EXEMPTION_EXPIRES_REQUIRED: { status: 400, retryable: false },
  // Emergency exemption (router#2007): reason is required so the open
  // can be reconstructed after the fact.
  CREDIT_EXEMPTION_REASON_REQUIRED: { status: 400, retryable: false },
  // Emergency exemption (router#2007): expiry must be strictly after the
  // open instant. Equal-to-now is already expired and is refused.
  CREDIT_EXEMPTION_EXPIRES_NOT_FUTURE: { status: 400, retryable: false },
  // Emergency exemption revoke (router#2032): no unexpired row to close.
  CREDIT_EXEMPTION_NOT_LIVE: { status: 409, retryable: false },
  // Password-set anti-escalation (router#2032): the target outranks the
  // caller. Distinct from FORBIDDEN so the denial is not confused with
  // the route-level ops-admin gate.
  OPS_USER_PASSWORD_ROLE_DENIED: { status: 403, retryable: false },
  // Retired by router#1999: submit never activates a line, so the
  // within-defaults single-sign downgrade guard no longer fires. Kept
  // on the catalog so historical audit rows and old clients still decode.
  REQUIRES_DUAL_SIGN: { status: 409, retryable: false },
  // Four-eyes violation on a multi-stage approval (epic #1033 E4-C2,
  // #1126): the deciding actor already decided a PRIOR stage of the same
  // request (they appear in `stage_decisions`). sales_lead and
  // finance_manager are independently assignable with no mutual
  // exclusivity, so one dual-role human could otherwise satisfy both
  // stages of a dual-sign credit application — this enforces a DISTINCT
  // human per stage. The request stays pending for a different eligible
  // approver.
  APPROVAL_STAGE_ACTOR_CONFLICT: { status: 409, retryable: false },
  RECEIPT_ORDER_NOT_FOUND: { status: 404, retryable: false },
  // Losing side of a concurrent decision on the same receipt (epic
  // #1031 E2-R1): the conditional `UPDATE … WHERE status='submitted'`
  // gate matched zero rows because another actor's terminal transition
  // already committed. Not retryable — the receipt has left `submitted`.
  RECEIPT_ORDER_NOT_SUBMITTED: { status: 409, retryable: false },
  // Invoice decisions couple to the RECEIPT lifecycle (epic #1031
  // E2-R6): issue/void are only valid on a `credited` receipt — a tax
  // invoice must never be issued against money that was never credited.
  // Not retryable as-is; finance retries after the receipt is credited.
  RECEIPT_ORDER_NOT_CREDITED: { status: 409, retryable: false },
  // Losing side of a concurrent invoice decision on the same receipt
  // (epic #1031 E2-R6): the conditional `UPDATE … WHERE
  // invoice_status='open'` gate matched zero rows — either another
  // finance actor's issue/void already committed, or the receipt never
  // needed an invoice (`invoice_status='none'`). Not retryable.
  RECEIPT_INVOICE_NOT_OPEN: { status: 409, retryable: false },
  // Fail-closed guard (epic #1030 E1-S4): correcting a customer's
  // signup_brand while a foreign-brand customer manager actively owns
  // the customer would silently strand the book; reassign first.
  SIGNUP_BRAND_CONFLICT: { status: 409, retryable: false },
  CHANNEL_NOT_FOUND: { status: 404, retryable: false },
  CHANNEL_ALREADY_EXISTS: { status: 409, retryable: false },
  CHANNEL_ALIAS_ALREADY_EXISTS: { status: 409, retryable: false },
  CHANNEL_MODEL_NOT_FOUND: { status: 404, retryable: false },
  CHANNEL_MODEL_ALREADY_EXISTS: { status: 409, retryable: false },
  // #1374: activation-plan mutations and org permission rows FK-reference
  // marketplace_models without cascade; a referenced SKU cannot be deleted
  // and the operator gets the blocking counts instead of a raw 500.
  CHANNEL_MODEL_DEPENDENCIES_EXIST: { status: 409, retryable: false },
  VENDOR_NOT_FOUND: { status: 404, retryable: false },
  VENDOR_ALREADY_EXISTS: { status: 409, retryable: false },
  VENDOR_OFFICIAL_KEY_NOT_FOUND: { status: 404, retryable: false },
  VENDOR_DISCOVERED_MODEL_NOT_FOUND: { status: 404, retryable: false },
  VENDOR_DISCOVERED_MODEL_NOT_MANUAL: { status: 409, retryable: false },
  VENDOR_DEPENDENCIES_EXIST: { status: 409, retryable: false },
  KEY_BUCKET_NOT_FOUND: { status: 404, retryable: false },
  UPSTREAM_CAPABILITY_NOT_FOUND: { status: 404, retryable: false },
  // Exact capability ignore would remove the final live canonical
  // capability behind an enabled SKU target, or the capability is still
  // referenced by a live SKU target or global default (#1157).
  UPSTREAM_CAPABILITY_IN_USE: { status: 409, retryable: false },
  UPSTREAM_CAPABILITY_NOT_ROUTABLE: { status: 409, retryable: false },
  // A route target exists but the exact upstream/channel/SKU procurement
  // multiplier is missing. There is deliberately no channel-wide or 1x
  // fallback after the SKU-grain cutover.
  UPSTREAM_SKU_COST_NOT_CONFIGURED: { status: 409, retryable: false },
  ROUTE_BINDING_NOT_FOUND: { status: 404, retryable: false },
  ROUTE_BINDING_ALREADY_EXISTS: { status: 409, retryable: false },
  // A target-probe reset / probe-now named a leg in another region. Probe
  // state is region-local (#2604): this gateway can neither write nor probe
  // the other region's row, so it refuses instead of silently acting locally.
  TARGET_PROBE_REGION_MISMATCH: { status: 409, retryable: false },
  // The caller's upstream routing revision no longer matches the durable
  // upstream snapshot. The caller must reload from page zero before writing.
  ROUTING_SNAPSHOT_STALE: { status: 409, retryable: false },
  // Destructive replacement gate: the write would cascade-delete routing
  // objects (SKU targets / global defaults) and the request did
  // not carry `confirm_cascade: true`. Not retryable as-is — the caller
  // must re-submit with explicit confirmation after showing the operator
  // the structured impact list in the response body.
  CASCADE_CONFIRMATION_REQUIRED: { status: 409, retryable: false },
  // Org-scoped routing-content writes are permanently rejected (epic #852):
  // routing targets/fallbacks/policies are maintained on routing configs,
  // and the only org-level action is switching the assigned config. 403
  // (not 404/405) so misusing callers get a diagnosable governance error.
  ORG_ROUTING_WRITE_FORBIDDEN: { status: 403, retryable: false },
  // Mutations (e.g. rename) against an archived routing config. 409 because
  // the config exists but its lifecycle state forbids the operation.
  ROUTE_CONFIG_ARCHIVED: { status: 409, retryable: false },
  ROUTE_CONFIG_DEPRECATED: { status: 409, retryable: false },
  // No `routing_dr_plans` marker for the requested routing config (#1421).
  // Distinct from ROUTE_BINDING_NOT_FOUND so an operator can tell "this config
  // does not exist" from "this config exists but is not a DR plan".
  ROUTING_DR_PLAN_NOT_FOUND: { status: 404, retryable: false },
  // A `preflight_token` was supplied to a global switch but no longer matches
  // the live config state it pinned (#1421). Not retryable as-is: the operator
  // must re-run the preflight and re-read the report before switching.
  ROUTING_PREFLIGHT_TOKEN_STALE: { status: 409, retryable: false },
  // Rollback was requested but `routing_switch_events` holds no global switch
  // with a previous config to return to (#1421).
  ROUTING_SWITCH_NO_ROLLBACK_TARGET: { status: 409, retryable: false },
  // A rollback resolved its target from the event log, then found under the
  // assignment row lock that the live global config is no longer the one that
  // log head switched to — another switch landed in between (#1421).
  // Deliberately not retryable: a blind retry would compute a new target from
  // a log the operator has not re-read, which is how a rollback silently
  // undoes two steps instead of one.
  ROUTING_SWITCH_HEAD_MOVED: { status: 409, retryable: false },
  ROUTING_OVERRIDE_NOT_ENGAGED: { status: 409, retryable: false },
  // Service levels (#1723/#1724). A level id that does not exist, and the
  // two lifecycle refusals kept distinct from the routing-config ones so an
  // operator can tell a level problem from a route-config problem.
  SERVICE_LEVEL_NOT_FOUND: { status: 404, retryable: false },
  SERVICE_LEVEL_NAME_TAKEN: { status: 409, retryable: false },
  // Archived levels stay readable and keep their existing members, but no
  // new org may be moved onto one.
  SERVICE_LEVEL_ARCHIVED: { status: 409, retryable: false },
  // Delete is only for a level nobody sits on; archiving is the soft path.
  SERVICE_LEVEL_IN_USE: { status: 409, retryable: false },
  // A customer manager tried to set a level on an org an admin has taken
  // manual control of. 409, not 403: the role is allowed to do this, the
  // CUSTOMER is simply no longer level-managed. Reloading shows why.
  SERVICE_LEVEL_ORG_ADMIN_MANAGED: { status: 409, retryable: false },
  // Custom org roles (tenant #178). Name uniqueness is per-org; delete is
  // refused while any member (or pending invitation) still hangs on the row.
  ORG_ROLE_NOT_FOUND: { status: 404, retryable: false },
  ORG_ROLE_NAME_TAKEN: { status: 409, retryable: false },
  ORG_ROLE_IN_USE: { status: 409, retryable: false },
  // Admission firewall rulesets (#1758). A ruleset is a stored judgement about
  // live traffic, so every refusal here is a write-face refusal: nothing in
  // this family is reachable from a tenant request path.
  FIREWALL_RULESET_NOT_FOUND: { status: 404, retryable: false },
  FIREWALL_RULESET_VERSION_NOT_FOUND: { status: 404, retryable: false },
  FIREWALL_RULESET_NAME_TAKEN: { status: 409, retryable: false },
  // Write-time validation failed. The response body carries the FULL issue
  // list, not the first one: a rule author fixing one problem per round trip
  // is a rule author who stops using the validator.
  FIREWALL_RULESET_INVALID: { status: 400, retryable: false },
  // Delete refused because versions or child rulesets still reference it.
  FIREWALL_RULESET_IN_USE: { status: 409, retryable: false },
  // Activation refused: no replay record exists for the chain about to be
  // armed, measured against the version armed right now (#1759). A PROCESS
  // gate -- it asserts the comparison was produced, never that it was clean.
  FIREWALL_RULESET_REPLAY_REQUIRED: { status: 409, retryable: false },
  EGRESS_PROFILE_NOT_FOUND: { status: 404, retryable: false },
  EGRESS_PROFILE_ALREADY_EXISTS: { status: 409, retryable: false },
  EGRESS_PROFILE_IN_USE: { status: 409, retryable: false },
  EGRESS_POLICY_NOT_FOUND: { status: 404, retryable: false },
  EGRESS_POLICY_IN_USE: { status: 409, retryable: false },
  EGRESS_POLICY_VERSION_CONFLICT: { status: 409, retryable: false },
  // Heartbeat for an unregistered egress-exit link (#1967 R1): the exit backs
  // off and re-registers, so this is a routine signal rather than a fault.
  EGRESS_LINK_NOT_FOUND: { status: 404, retryable: false },
  EGRESS_TEMPLATE_NOT_FOUND: { status: 404, retryable: false },
  EGRESS_TEMPLATE_ALREADY_EXISTS: { status: 409, retryable: false },
  EGRESS_TEMPLATE_IN_USE: { status: 409, retryable: false },
  PROBE_JOB_NOT_FOUND: { status: 404, retryable: false },
  PROBE_JOB_NOT_CLAIMABLE: { status: 409, retryable: true },
  PROBE_JOB_CLAIM_CONFLICT: { status: 409, retryable: true },
  PROBE_JOB_NOT_RUNNING: { status: 409, retryable: false },
  MESSAGE_TRACE_NOT_FOUND: { status: 404, retryable: false },
  // The row is there, but the bucket or the decryption key is not. A 404
  // would tell the operator the trace is gone.
  MESSAGE_TRACE_STORAGE_UNCONFIGURED: { status: 503, retryable: false },
  REQUEST_LIFECYCLE_NOT_FOUND: { status: 404, retryable: false },
  FUNDING_BATCH_NOT_FOUND: { status: 404, retryable: false },
  CUSTOMER_ALREADY_CLAIMED: { status: 409, retryable: false },
  ORG_NOT_FOUND: { status: 404, retryable: false },
  INVITATION_NOT_FOUND: { status: 404, retryable: false },
  INVITATION_CONFLICT: { status: 409, retryable: false },
  KEY_PRESET_VERSION_CONFLICT: { status: 409, retryable: true },
  KEY_PRESET_OPERATION_NOT_FOUND: { status: 404, retryable: false },
  KEY_PRESET_MAPPING_CONFLICT: { status: 409, retryable: false },
  KEY_PRESET_OPERATION_CONFLICT: { status: 409, retryable: false },
  KEY_PRESET_ROLLBACK_CONFLICT: { status: 409, retryable: false },
  SKU_ACTIVATION_ENVIRONMENT_MISMATCH: { status: 409, retryable: false },
  SKU_ACTIVATION_SCOPE_MISMATCH: { status: 409, retryable: false },
  SKU_ACTIVATION_PLAN_NOT_FOUND: { status: 404, retryable: false },
  SKU_ACTIVATION_PLAN_CONFLICT: { status: 409, retryable: false },
  INVITATION_EXPIRED: { status: 410, retryable: false },
  INVITATION_REVOKED: { status: 410, retryable: false },
  INVITATION_ALREADY_ACCEPTED: { status: 409, retryable: false },
  INVALID_VERIFICATION_TOKEN: { status: 400, retryable: false },
  VERIFICATION_TOKEN_EXPIRED: { status: 400, retryable: false },
  VERIFICATION_TOKEN_CONSUMED: { status: 400, retryable: false },
  // Invite-code activation + email login codes (epic #1475). These six wire
  // names are the requirements-doc client contract, kept verbatim so any
  // client built against the doc (web consoles now, desktop later) branches
  // on the same codes. Unknown and disabled invite codes share one code on
  // purpose — activation callers get no probe signal for code state.
  INVITE_CODE_INVALID: { status: 400, retryable: false },
  // Daily slot consumed; retryable because the quota resets at UTC+8
  // midnight — clients render "try tomorrow", not an automatic retry.
  INVITE_CODE_QUOTA_EXHAUSTED: { status: 429, retryable: true },
  // Returned by POST /v1/auth/register and POST /v1/auth/activate so
  // consoles can route the user to login instead of showing a validation
  // error. Register used to send INVALID_PAYLOAD here, which the console
  // could only render as a generic "invalid submission".
  EMAIL_ALREADY_REGISTERED: { status: 409, retryable: false },
  // POST /v1/auth/register field checks, each with its own code so clients
  // name the field at fault. INVALID_PAYLOAD stays the fallback for bodies
  // the server cannot attribute to one field (e.g. not JSON).
  EMAIL_FORMAT_INVALID: { status: 400, retryable: false },
  PASSWORD_LENGTH_INVALID: { status: 400, retryable: false },
  // Refused by POST /v1/auth/password/set/code and POST /v1/auth/password/set
  // — the account already has a password; use /v1/auth/password/change or
  // the forgot/reset flow instead.
  PASSWORD_ALREADY_SET: { status: 409, retryable: false },
  // Also the anti-enumeration alias for unknown emails on verify — a wrong
  // code and a nonexistent account are indistinguishable on the wire.
  CODE_INVALID: { status: 400, retryable: false },
  CODE_EXPIRED: { status: 400, retryable: false },
  CODE_RATE_LIMITED: { status: 429, retryable: true },
  // Ops CRUD (/internal/invite-codes): unlike the tenant-facing
  // INVITE_CODE_INVALID above, operators may distinguish a bad id.
  INVITE_CODE_NOT_FOUND: { status: 404, retryable: false },
  // Caller-supplied invite-code text failed the shared `<PREFIX>-<SUFFIX>`
  // shape (trim + upper-case, brand prefix, suffix charset and length).
  INVITE_CODE_FORMAT_INVALID: { status: 400, retryable: false },
  // Caller-supplied text already occupies `invite_codes`, `promoter_codes`,
  // or `gift_cards`. Custom codes are never retried with other text.
  INVITE_CODE_TAKEN: { status: 409, retryable: false },
  // Rebind of a plan onto a code that still carries 0064 one-shot
  // `invite_code_rate_discounts` rows. Mutual exclusion is mint-time XOR;
  // this 409 is the later-rebind twin so the ops-console can offer a
  // migrate flow instead of silently dropping the snapshot discounts.
  INVITE_CODE_HAS_LEGACY_DISCOUNTS: { status: 409, retryable: false },
  // POST /internal/invite-codes/:id/plan-from-discounts when the code is
  // already bound to a model plan. The migrate helper is a one-shot
  // close-the-loop for leftover 0064 rows, not a rebind.
  INVITE_CODE_ALREADY_HAS_PLAN: { status: 409, retryable: false },
  // 2C promoter rebate (epic #2456 / #2457). Tenant-facing bind/mint
  // codes plus the promoter-portal position and team-management codes.
  // Distinct from INVITE_CODE_* — promoter codes live in their own
  // table and a collision with an invite code is CODE_TAKEN, not the
  // activation INVALID. Retryable only for remint-too-soon (the 30-day
  // window elapses).
  PROMOTER_CODE_INVALID: { status: 400, retryable: false },
  PROMOTER_ALREADY_BOUND: { status: 409, retryable: false },
  PROMOTER_BIND_WINDOW_CLOSED: { status: 409, retryable: false },
  PROMOTER_SELF_BIND_FORBIDDEN: { status: 403, retryable: false },
  PROMOTER_BIND_CONFLICT: { status: 409, retryable: false },
  PROMOTER_POSITION_REQUIRED: { status: 403, retryable: false },
  PROMOTER_POSITION_READ_ONLY: { status: 403, retryable: false },
  PROMOTER_CODE_TAKEN: { status: 409, retryable: false },
  PROMOTER_CODE_BLACKLISTED: { status: 400, retryable: false },
  PROMOTER_CODE_REMINT_TOO_SOON: { status: 429, retryable: true },
  PROMOTER_SPLIT_RATIO_INVALID: { status: 400, retryable: false },
  PROMOTER_TARGET_NOT_FOUND: { status: 404, retryable: false },
  PROMOTER_TARGET_HOLDS_POSITION: { status: 409, retryable: false },
  PROMOTER_AGREEMENT_REQUIRED: { status: 403, retryable: false },
  // Model plans (epic #2292 / #2294). Distinct from INVITE_CODE_* so an
  // operator can tell a missing plan from a missing code, and distinct
  // from SKU_RATE_BELOW_SALES_FLOOR because that code means "convert this
  // org write into a discount_below_sales_cost approval". Plan writes have
  // no approval hatch — admin does not bypass, a miss is a hard 409.
  MODEL_PLAN_NOT_FOUND: { status: 404, retryable: false },
  // CRM quotation draft missing, or owned by a different ops user. Same
  // response for both so a list of ids cannot probe another owner's book.
  QUOTATION_NOT_FOUND: { status: 404, retryable: false },
  MODEL_PLAN_NAME_CONFLICT: { status: 409, retryable: false },
  MODEL_PLAN_ARCHIVED: { status: 409, retryable: false },
  MODEL_PLAN_HAS_ACTIVE_CODES: { status: 409, retryable: false },
  MODEL_PLAN_RATE_BELOW_SALES_FLOOR: { status: 409, retryable: false },
  MODEL_PLAN_SKU_OUTSIDE_GROUPS: { status: 400, retryable: false },
  // Plan-bound org: every org-level model-config write is refused so the
  // operator edits the plan instead. Payload carries model_plan_id +
  // model_plan_name for the console hint ("请修改相应的模型方案").
  MODEL_PLAN_MANAGED: { status: 409, retryable: false },
  EMAIL_NOT_VERIFIED: { status: 403, retryable: false },
  PAYMENT_ORDER_NOT_FOUND: { status: 404, retryable: false },
  // Raised by `/internal/ops/payment-providers/:id/*` when the provider
  // config row doesn't exist. 404 because the resource is ops-visible —
  // the operator should be able to tell "I typed a bad id" apart from
  // "the backend is broken".
  PAYMENT_PROVIDER_CONFIG_NOT_FOUND: { status: 404, retryable: false },
  TOPUP_AMOUNT_TOO_SMALL: { status: 400, retryable: false },
  // Upper bound ($200 = 20000 USD cents in v1-billing). Surfaced as a
  // distinct code (not INVALID_PAYLOAD) so the console-web UI can render
  // a "contact sales for larger topups" message without string-matching.
  TOPUP_AMOUNT_TOO_LARGE: { status: 400, retryable: false },
  // 502 so callers (console-web) distinguish upstream provider errors from
  // our own server bugs (500 INTERNAL). Payment provider is external; the
  // retry policy is conservative `retryable: true` because the typical cause
  // is transient network or ZPay queue blips.
  TOPUP_PROVIDER_ERROR: { status: 502, retryable: true },
  INVALID_PAYLOAD: { status: 400, retryable: false },
  ARCHIVED_WINDOW_FROZEN: { status: 409, retryable: false },
  // Commission rate append rejected for an effective_from before the
  // current UTC+8 period — a dedicated code so the ops-console renders the
  // "no retroactive changes" guidance instead of a generic validation blip
  // (epic #1034 E5-M2, #1144).
  COMMISSION_RATE_RETROACTIVE: { status: 400, retryable: false },
  // A commission rate already exists for this (brand, effective_from) —
  // append-only history is one row per period; the collision mirrors
  // model_pricing's MODEL_ALREADY_EXISTS (#1144).
  COMMISSION_RATE_ALREADY_EXISTS: { status: 409, retryable: false },
  // Commission settlement workflow (epic #1034 E5-M4, #1146). The
  // confirm/adjust/payout endpoints are conditional so concurrent callers
  // resolve to distinct outcomes rather than silently double-applying.
  // COMMISSION_STATEMENT_NOT_FOUND is also the M5 report detail 404 (#1147).
  COMMISSION_STATEMENT_NOT_FOUND: { status: 404, retryable: false },
  // Confirm ran against a statement that is no longer 'draft' — the
  // concurrency loser of a confirm race, or a re-confirm attempt.
  COMMISSION_STATEMENT_NOT_DRAFT: { status: 409, retryable: false },
  // Confirm ran against a draft whose period has not ended yet. The accrual
  // keeps a live draft for the CURRENT open month; confirming it freezes the
  // period and silently drops every later same-period money event, so a
  // still-open period must never be confirmed.
  COMMISSION_STATEMENT_PERIOD_OPEN: { status: 409, retryable: false },
  // Adjustment / payout ran against a statement that is not yet
  // 'confirmed' — post-lock corrections and payout markers only attach to
  // confirmed statements.
  COMMISSION_STATEMENT_NOT_CONFIRMED: { status: 409, retryable: false },
  // Payout marker is set-once: a second payout attempt loses the
  // conditional UPDATE (paid_out_at IS NULL) and observes this.
  COMMISSION_STATEMENT_ALREADY_PAID_OUT: { status: 409, retryable: false },
  // An adjustment idempotency key was reused with a DIFFERENT amount or
  // reason (bug #1181 R6). An exact replay returns the existing row
  // (200, replayed: true); a divergent reuse is a client bug that must
  // neither double-book nor silently no-op — surface it.
  COMMISSION_ADJUSTMENT_IDEMPOTENCY_CONFLICT: { status: 409, retryable: false },
  // Ledger archive day retry (#2695 PR-C): the day does not exist or is not
  // stopped on a terminal error, so there is nothing to re-queue. Transient
  // errors already retry on their own backoff.
  LEDGER_ARCHIVE_DAY_NOT_STOPPED: { status: 409, retryable: false },
  // restart_rev on a code it cannot resolve, or on a day that still holds an
  // unpublished manifest reservation (its pub_seq may already be in OSS).
  LEDGER_ARCHIVE_RESTART_REFUSED: { status: 409, retryable: false },
  // l1-release (#2695 PR-F) on a day that is not verified with a done, red L1
  // whose every l1_red event is classified (the message names which).
  LEDGER_ARCHIVE_L1_RELEASE_REFUSED: { status: 409, retryable: false },
  // Re-export request consumption (#2560 / #2695) on a day that is not verified /
  // shadow_cut with no last_error, or naming requests that are not the day's open
  // ones, or a restart the day cannot take (cut, reservation, reaped, floor).
  LEDGER_ARCHIVE_REEXPORT_REFUSED: { status: 409, retryable: false },
  // Writer freeze line operator surface (#2695 FL-1): unknown writer; a
  // change the database rules refuse (an immediate lowering of W for any
  // writer but unpaid, W_a5 < W_attr, a step back of a forward-only writer,
  // …); a confirmation that does not name the writer and the mode it would
  // end in.
  LEDGER_WRITE_WINDOW_NOT_FOUND: { status: 404, retryable: false },
  LEDGER_WRITE_WINDOW_REFUSED: { status: 409, retryable: false },
  LEDGER_WRITE_WINDOW_CONFIRMATION_MISMATCH: { status: 400, retryable: false },
  // A-04 on (#2561): an unsealed day before the shadow cut has no online
  // `revenue_ledger` row, and `ledger_fold_completion.charges_deleted` is
  // set. The charge deleter removed that day without a
  // `revenue_daily_rollup` seal, so a zero would invent a total. A day
  // with neither rows nor `charges_deleted` is empty and is zero-filled
  // instead. Not retryable — sealing the rollup is what unblocks the read.
  REVENUE_DAILY_UNAVAILABLE: { status: 409, retryable: false },
  // A-19 on (#2561): a shadow_cut day in the PnL window has no
  // finance_pnl_usage_daily_rollup_days row. Answering 0 would drop usage
  // the deleter already removed from revenue_ledger. Not retryable — the
  // daily seal is what unblocks the read.
  PNL_USAGE_DAY_UNSEALED: { status: 409, retryable: false },
  // Revenue hour readers on (#2561 A-01 / A-08 / A-09 / A-10 / A-11 /
  // A-12 / A-13) and the customer funnel (`LEDGER_FUNNEL_READ`, A-23): a
  // partial hour at either end of the window falls on a booking day whose
  // `ledger_fold_completion.charges_deleted` is set. The online rows are
  // gone and `ledger_shadow_h1` has no sub-hour grain, so a number would
  // under-count. Not retryable — the caller narrows the window to whole
  // UTC hours. The switch off keeps the pre-change statement and does not
  // return this code.

  REVENUE_PARTIAL_HOUR_UNAVAILABLE: { status: 409, retryable: false },
  INVALID_CREDENTIALS: { status: 401, retryable: false },
  OPS_USER_DISABLED: { status: 403, retryable: false },
  FORBIDDEN: { status: 403, retryable: false },
  // PR-7 CD discovery: a deploy_target=1 registry row lacks a valid IPv4
  // deploy_ip. 500 (not 4xx): the registry is server-side state and a
  // partial host list must never reach a rolling deploy.
  VPS_DISCOVERY_MISSING_DEPLOY_IP: { status: 500, retryable: false },
  NOT_IMPLEMENTED: { status: 501, retryable: false },
  RATE_LIMITED: { status: 429, retryable: true },
  SERVICE_UNAVAILABLE: { status: 503, retryable: true },
  HISTORY_READ_COLD_UNSUPPORTED: { status: 503, retryable: true },
  HISTORY_READ_TIMEOUT: { status: 503, retryable: false },
  INTERNAL: { status: 500, retryable: true },
};

export const ERROR_CODES = Object.freeze(Object.keys(ERROR_DEFAULTS) as ErrorCode[]);
